// src/components/PrescriptionQueue.tsx
import React, { useEffect, useState } from "react";
import PrescriptionApprovalModal from "./modals/PrescriptionApprovalModal";
import PrescriptionDeniedModal from "./modals/PrescriptionDeniedModal";
import { useValidateNpi } from "../hooks/useValidateNpi";
import PrescriptionCard from "./PrescriptionCard";
import IDVerificationIndicator from "./IDVerificationIndicator";
import VerifyIdButton from "./modals/VerifyIdButton";
import { Prescription } from "../types/prescription";
import { Provider } from "../types/user";
import "../styles/prescriptions/prescriptionQueue.css";
import { fetchWithAuth } from "../utils/fetchWithAuth";
import { SERVER_URL } from "../config";

interface Props {
  prescriptions: Prescription[];
  fetchNextBatch: () => void;
  provider: Provider;
}

const PrescriptionQueue: React.FC<Props> = ({
  prescriptions,
  fetchNextBatch,
  provider,
}) => {
  const [displayed, setDisplayed] = useState<Prescription[]>(prescriptions);
  const [idx, setIdx] = useState(0);
  const [showApprove, setShowApprove] = useState(false);
  const [showDeny, setShowDeny] = useState(false);
  const [reviewed, setReviewed] = useState(false);
  const [askId, setAskId] = useState(false);

  const [firstName, setFirst] = useState("");
  const [lastName, setLast] = useState("");
  const [email, setEmail] = useState("");
  const [region, setRegion] = useState("");
  const [results, setResults] = useState<Prescription[]>([]);
  const token = localStorage.getItem("token");

  const runSearch = async () => {
    const qs = new URLSearchParams({
      firstName,
      lastName,
      email,
      region,
    }).toString();
    if (!qs.replace(/=(&|$)/g, "")) return;
    const res = await fetchWithAuth(
      `${SERVER_URL}/api/prescription/search?${qs}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (res.ok) setResults(await res.json());
  };

  useEffect(() => {
    setDisplayed(prescriptions);
    setIdx(0);
  }, [prescriptions]);

  useEffect(() => {
    if (idx >= displayed.length) setIdx(0);
  }, [displayed.length]);

  const { valid: npiValid } = useValidateNpi(provider.npi!);
  if (!npiValid)
    return (
      <div className="prescription-queue-container">
        <h1>Please update your NPI</h1>
        <h3>An active license is required to proceed.</h3>
      </div>
    );

  // Filter only prescriptions with status 'REQUESTED'
  const requestedPrescriptions = displayed.filter(
    (rx) => rx.status === "REQUESTED"
  );

  if (requestedPrescriptions.length === 0)
    return (
      <div className="prescription-queue-container">
        <h1>No pending prescriptions.</h1>
      </div>
    );

  const current = requestedPrescriptions[idx];
  const pp = current.patient.patientProfile;
  const entry = current.prescriberEntry ?? {
    dosage: "",
    quantity: 1,
    refills: 1,
    instructions: "",
  };

  const next = () => {
    if (!reviewed) return alert("Please approve or deny before next.");
    if (idx < requestedPrescriptions.length - 1) {
      setIdx((i) => i + 1);
      setReviewed(false);
    } else fetchNextBatch();
  };

  return (
    <div className="prescription-queue-container">
      <div className="prescription-main-content">
        <div className="prescription-top">
          <IDVerificationIndicator
            verified={pp.isIDverified || false}
            idImageUrl={pp.idFront}
            selfieImageUrl={pp.selfie}
          />
          <div className="search-bar">
            <span className="search-field">
              firstName:
              <input
                value={firstName}
                onChange={(e) => setFirst(e.target.value)}
              />
            </span>
            <span className="search-field">
              lastName:
              <input
                value={lastName}
                onChange={(e) => setLast(e.target.value)}
              />
            </span>
            <span className="search-field">
              email:
              <input value={email} onChange={(e) => setEmail(e.target.value)} />
            </span>
            <span className="search-field">
              stateCode:
              <input
                value={region}
                onChange={(e) => setRegion(e.target.value)}
              />
            </span>
            <button className="search-btn" onClick={runSearch}>
              Search
            </button>
          </div>
        </div>
        {results.length > 0 && (
          <ul className="search-results">
            {results.map((rx) => (
              <li
                key={rx.id}
                onClick={() => {
                  setResults([]);
                  setFirst("");
                  setLast("");
                  setEmail("");
                  setRegion("");
                  setDisplayed([rx, ...displayed]);
                  setIdx(0);
                }}
              >
                {rx.patient.patientProfile
                  ? `${rx.patient.patientProfile.lastName}, ${rx.patient.patientProfile.firstName} – ${rx.patient.patientProfile.region} - ${rx.patient.email}`
                  : rx.patient.email}
              </li>
            ))}
          </ul>
        )}

        <PrescriptionCard prescription={current} />

        <PrescriptionApprovalModal
          isOpen={showApprove}
          onClose={() => {
            setShowApprove(false);
            setReviewed(true);
          }}
          prescription={current}
          provider={provider}
        />

        <PrescriptionDeniedModal
          isOpen={showDeny}
          onClose={() => {
            setShowDeny(false);
            setReviewed(true);
          }}
          prescription={{
            prescriptionId: current.id,
            medicationName: current.medication.name,
            dosage: entry.dosage,
            refills: entry.refills,
            quantity: entry.quantity,
            instructions: entry.instructions,
          }}
          patientName={`${pp.firstName} ${pp.lastName}`}
          providerName={`${provider.firstName} ${provider.lastName}`}
          providerUserId={provider.userId}
          patientProfileId={pp.id}
          clinicId={provider.clinic.id}
        />

        {askId && (
          <VerifyIdButton
            isOpen={askId}
            patientFirstName={pp.firstName!}
            patientId={pp.id}
            providerProfile={provider}
            onClose={() => setAskId(false)}
          />
        )}
      </div>

      <div className="bottom-bar">
        <button
          onClick={() => setShowApprove(true)}
          className="prescription-button"
        >
          Approve
        </button>
        <button
          onClick={() => setShowDeny(true)}
          className="prescription-button"
        >
          Deny
        </button>
        <button onClick={next} className="prescription-button">
          Next
        </button>
        <button onClick={() => setAskId(true)} className="prescription-button">
          Request ID
        </button>
        <span className="prescription-button">
          <a
            href={`/patient-profile/${pp.id}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            View Patient Profile
          </a>
        </span>
      </div>
    </div>
  );
};

export default PrescriptionQueue;
